import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, Heart, ShieldCheck, Sparkles, Clock, ArrowRight, Phone, HeartHandshake } from "lucide-react";
import { CARE_TYPES } from "@/lib/api";
import QuickHelpDialog from "@/components/QuickHelpDialog";

const HERO_IMG =
  "https://images.pexels.com/photos/7551684/pexels-photo-7551684.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";
const SECONDARY_IMG =
  "https://images.unsplash.com/photo-1764173039543-f9f197744e1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxjb3p5JTIwbGl2aW5nJTIwcm9vbSUyMHNlbmlvciUyMGNvbW11bml0eXxlbnwwfHx8fDE3ODAxNDU3NzB8MA&ixlib=rb-4.1.0&q=85&w=900";
const SUPPORT_IMG =
  "https://images.pexels.com/photos/18509796/pexels-photo-18509796.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=900";

export default function Landing() {
  const navigate = useNavigate();
  const [location, setLocation] = useState("");
  const [careType, setCareType] = useState("");
  const [memoryOnly, setMemoryOnly] = useState(false);

  useEffect(() => {
    document.title = "Providence Partners — Adult Day, Memory Care & Daily Hospice Respite for Families Seeking Care";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "For families seeking care nationwide. Find trusted adult day care, memory care, respite, autism/IDD homes, and daily-rate hospice respite — curated by real human advisors, never a lead-mill.",
      );
    }
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (location.trim()) {
      const v = location.trim();
      if (/^\d{5}$/.test(v)) params.set("zip", v);
      else params.set("city", v);
    }
    if (careType) params.set("care_type", careType);
    if (memoryOnly) params.set("specialty", "memory_care");
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div data-testid="landing-page" className="bg-background">
      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src={HERO_IMG}
            alt="A daughter sitting with her elderly father in a warm, sunlit home"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-sand/95 via-sand/70 to-transparent" />
        </div>

        <div className="mx-auto grid max-w-7xl gap-12 px-6 pb-24 pt-20 lg:grid-cols-12 lg:px-8 lg:pt-28">
          <div className="lg:col-span-7">
            <Badge
              variant="secondary"
              className="rounded-full bg-secondary/80 px-3 py-1 text-xs font-medium text-secondary-foreground"
              data-testid="hero-badge"
            >
              <Sparkles className="mr-1.5 h-3 w-3" /> A Providence Partners service · Members-only directory
            </Badge>
            <h1 className="mt-5 font-serif text-5xl leading-[1.05] tracking-tight text-forest sm:text-6xl lg:text-[68px]">
              Adult Day Care &{" "}
              <span className="text-primary">Memory Care</span>{" "}
              — try a place before you commit.
            </h1>
            <p className="mt-4 max-w-xl text-base italic text-gold sm:text-lg">
              Timely Preparation for Future Eventualities.
            </p>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-muted-foreground">
              Providence Partners helps working caregivers locate trusted adult day centers,
              dedicated memory care, drop-in care, and short respite stays nearby. We also list
              homes that specialize in <b className="text-forest">autism and IDD</b> support, and
              <b className="text-forest"> daily-rate hospice respite</b> in a home setting. Try a
              place first — then decide on senior placement with confidence.
            </p>

            <form
              onSubmit={submit}
              data-testid="hero-search-form"
              className="mt-8 rounded-2xl border border-border/60 bg-white/85 p-3 shadow-lg backdrop-blur-xl"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex flex-1 items-center gap-2 px-3">
                  <MapPin className="h-5 w-5 text-primary" strokeWidth={1.6} />
                  <Input
                    data-testid="hero-location-input"
                    placeholder="City or ZIP near you"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="h-12 border-0 bg-transparent text-base shadow-none focus-visible:ring-0"
                  />
                </div>
                <div className="hidden h-8 w-px bg-border sm:block" />
                <select
                  data-testid="hero-care-select"
                  value={careType}
                  onChange={(e) => setCareType(e.target.value)}
                  className="h-12 rounded-xl border-0 bg-transparent px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:w-56"
                >
                  <option value="">Any care type</option>
                  {CARE_TYPES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <Button
                  type="submit"
                  data-testid="hero-search-btn"
                  className="h-12 rounded-xl bg-primary px-6 text-primary-foreground shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md"
                >
                  <Search className="mr-2 h-4 w-4" /> Find a center
                </Button>
              </div>
              <label className="mt-3 flex cursor-pointer items-center gap-2 px-3 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={memoryOnly}
                  onChange={(e) => setMemoryOnly(e.target.checked)}
                  data-testid="hero-memory-care-toggle"
                  className="h-4 w-4 rounded border-border accent-primary"
                />
                <span className="font-medium text-emerald">Memory care available</span>
                <span className="text-muted-foreground">— only show homes with memory care specialists</span>
              </label>
            </form>

            {/* Above-the-fold lead intake CTA */}
            <div className="mt-4 rounded-2xl border border-primary/20 bg-gradient-to-r from-emerald/8 to-gold/8 p-4 sm:flex sm:items-center sm:gap-4">
              <div className="flex-1">
                <p className="font-serif text-lg text-forest">Not sure where to start?</p>
                <p className="text-sm text-muted-foreground">A real advisor will help you find care — free, no obligation, no lead-mill.</p>
              </div>
              <QuickHelpDialog sourceTag="landing_hero_above_fold">
                <Button
                  data-testid="hero-get-help-btn"
                  className="mt-3 w-full rounded-full bg-primary px-6 py-6 text-base text-primary-foreground shadow-md transition-transform hover:-translate-y-0.5 hover:shadow-lg sm:mt-0 sm:w-auto"
                >
                  <HeartHandshake className="mr-2 h-5 w-5" /> Get free help finding care <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </QuickHelpDialog>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-sage" /> Verified member homes only</span>
              <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-sage" /> Same-day drop-in availability</span>
              <span className="inline-flex items-center gap-2"><Heart className="h-4 w-4 text-sage" /> Memory care & secured units</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 overflow-hidden rounded-2xl border border-border/60 shadow-sm">
                  <img src={SECONDARY_IMG} alt="A cozy senior living room" className="aspect-[5/3] w-full object-cover" />
                </div>
                <div className="overflow-hidden rounded-2xl border border-border/60 shadow-sm">
                  <img src={SUPPORT_IMG} alt="Caregiver supporting a senior" className="aspect-square w-full object-cover" />
                </div>
                <Card className="flex flex-col justify-between rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
                  <p className="font-serif text-2xl text-forest leading-tight">
                    "We tried Prestige Health Management in Troy for a Tuesday — by month two, mom asked when she could go back."
                  </p>
                  <p className="mt-3 text-xs uppercase tracking-wide text-muted-foreground">— Working daughter, Royal Oak</p>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-sand">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <p className="text-sm uppercase tracking-[0.18em] text-primary">From real Google reviews</p>
          <h2 className="mt-3 max-w-3xl font-serif text-4xl leading-tight text-forest sm:text-5xl">
            People who trusted Stress Free Aging when it mattered most.
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3" data-testid="testimonials-grid">
            <Card data-testid="testimonial-0" className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
              <div className="mb-3 flex gap-0.5 text-gold" aria-label="5 out of 5 stars">
                {"★★★★★".split("").map((s, i) => (<span key={i}>{s}</span>))}
              </div>
              <p className="font-serif text-lg leading-snug text-forest">
                "We used Stress Free Aging for my dad and his wife, and let me tell you <strong>Amanda is just the sweetest, most caring person!</strong> This is such a stressful time in your life and she just made everything stress free! I love what she does and I hope more people use her services — truly is a godsend."
              </p>
              <div className="mt-5 border-t border-border/60 pt-4">
                <p className="font-medium text-foreground">Dorie Beccaccio</p>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Family caregiver · Google review</p>
              </div>
            </Card>

            <Card data-testid="testimonial-1" className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
              <div className="mb-3 flex gap-0.5 text-gold" aria-label="5 out of 5 stars">
                {"★★★★★".split("").map((s, i) => (<span key={i}>{s}</span>))}
              </div>
              <p className="font-serif text-lg leading-snug text-forest">
                "Amanda recently coordinated a resident placement at our <strong>assisted living facility in Troy</strong>, and the entire process was handled smoothly from start to finish. She went above and beyond by arranging hospice services prior to admission and coordinating with DME providers. Her communication, professionalism, and attention to detail made a big difference."
              </p>
              <div className="mt-5 border-t border-border/60 pt-4">
                <p className="font-medium text-foreground">Harris Cheema</p>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Assisted living partner, Troy · Google review</p>
              </div>
            </Card>

            <Card data-testid="testimonial-google-cta" className="flex flex-col justify-between rounded-2xl border border-emerald/40 bg-emerald/5 p-6 shadow-sm">
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-gold" />
                  <span className="text-xs font-medium uppercase tracking-[0.16em] text-emerald">Verified on Google</span>
                </div>
                <p className="font-serif text-2xl leading-snug text-forest">
                  Read every review for Stress Free Aging on Google.
                </p>
                <p className="mt-3 text-sm text-muted-foreground">
                  Every word above is from a real Google review. See the full collection — from families,
                  hospital discharge planners, and assisted living partners nationwide.
                </p>
              </div>
              <a
                href="https://share.google/4Qg0Et2H9gqEwzP4p"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-reviews-link"
                className="mt-5 inline-flex items-center justify-center gap-1 rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:-translate-y-0.5 transition-transform"
              >
                See all Google reviews <ArrowRight className="ml-1 h-4 w-4" />
              </a>
            </Card>
          </div>
        </div>
      </section>

      {/* Placement help CTA */}
      <section className="mx-auto max-w-7xl px-6 py-10 lg:px-8">
        <Card className="rounded-3xl border border-gold/40 bg-gradient-to-br from-gold/10 to-card p-8 shadow-sm sm:p-10" data-testid="placement-banner">
          <div className="grid items-center gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <p className="text-sm uppercase tracking-[0.18em] text-primary">
                <Sparkles className="mb-1 mr-1 inline h-3.5 w-3.5 text-gold" /> Looking for a permanent home?
              </p>
              <h2 className="mt-2 font-serif text-3xl text-forest sm:text-4xl">
                Talk to a Providence Partners advisor first.
              </h2>
              <p className="mt-3 max-w-2xl text-base text-muted-foreground">
                When it's time to think about senior placement — memory care, assisted living, or a
                small group home — our advisors guide you personally. No facility tours are
                auto-scheduled here. Every introduction is human.
              </p>
            </div>
            <div className="flex flex-col gap-3 lg:col-span-4 lg:items-end">
              <Button asChild className="rounded-full bg-primary px-6 py-6 text-base text-primary-foreground" data-testid="landing-placement-btn">
                <Link to="/placement-help">Get placement help <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <p className="text-xs text-muted-foreground lg:text-right">
                Free conversation. No commitment.
              </p>
            </div>
          </div>
        </Card>
      </section>

      {/* Why Providence */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
        <div className="grid items-end gap-8 md:grid-cols-3">
          <div className="md:col-span-2 max-w-2xl">
            <p className="text-sm uppercase tracking-[0.18em] text-primary">Why Providence Partners</p>
            <h2 className="mt-3 font-serif text-4xl leading-tight text-forest sm:text-5xl">
              A gentler way to find day care for your parent
            </h2>
            <p className="mt-4 text-base text-muted-foreground sm:text-lg">
              You should never have to choose senior placement from a brochure. Providence Partners
              is built so you can try a real day, a real respite weekend, and a real conversation —
              before deciding.
            </p>
          </div>
          <div className="hidden md:flex justify-end">
            <img
              src="/providence-partners-logo.png"
              alt="Providence Partners crest"
              className="h-32 w-32 rounded-full object-cover shadow-md ring-1 ring-emerald/30"
            />
          </div>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            {
              icon: <Heart className="h-5 w-5" />,
              title: "Memory care + hospice, findable",
              body:
                "Filter for homes with memory care specialists, secured memory units, Parkinson's-trained staff, autism/IDD-experienced teams, or hospice-certified daily respite — at a glance.",
            },
            {
              icon: <MapPin className="h-5 w-5" />,
              title: "Locate nearby drop-in",
              body:
                "Search by city or ZIP and see member homes with same-day availability for emergency drop-in care.",
            },
            {
              icon: <Clock className="h-5 w-5" />,
              title: "Trial before placement",
              body:
                "Use trial visits and short respite stays as a no-pressure way to evaluate the right long-term fit.",
            },
          ].map((f, i) => (
            <Card
              key={i}
              className="rounded-2xl border border-border/60 bg-card p-7 shadow-sm transition-transform hover:-translate-y-1"
              data-testid={`feature-card-${i}`}
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                {f.icon}
              </span>
              <h3 className="mt-5 font-serif text-2xl text-forest">{f.title}</h3>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">{f.body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-sand">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.18em] text-primary">How it works</p>
              <h2 className="mt-3 font-serif text-4xl leading-tight text-forest sm:text-5xl">
                Three small steps. One big peace of mind.
              </h2>
              <div className="mt-10 space-y-8">
                {[
                  { n: "01", title: "Search by location", body: "Enter your city or ZIP and the kind of care you need — drop-in, weekly respite, or a trial visit." },
                  { n: "02", title: "Visit a verified home", body: "Every facility is a vetted member: adult foster care, memory care, senior living, or group home." },
                  { n: "03", title: "Try before you commit", body: "Send a drop-in inquiry, schedule respite, then make the bigger placement decision when you’re ready." },
                ].map((s) => (
                  <div key={s.n} className="flex gap-5">
                    <span className="font-serif text-3xl text-primary">{s.n}</span>
                    <div>
                      <h4 className="font-serif text-xl text-forest">{s.title}</h4>
                      <p className="mt-1 text-base text-muted-foreground">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src={SECONDARY_IMG}
                alt="Seniors in a warm living room"
                className="aspect-[4/5] w-full rounded-2xl object-cover shadow-md"
              />
              <Card className="absolute -bottom-6 -left-6 hidden max-w-xs rounded-2xl border border-border/60 bg-card/95 p-5 shadow-lg backdrop-blur-md sm:block">
                <p className="font-serif text-xl text-forest leading-snug">
                  “Members-only means we know the homes before you walk in.”
                </p>
                <p className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">
                  Providence promise
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
        <Card className="overflow-hidden rounded-3xl border border-border/60 bg-card p-10 shadow-sm sm:p-14">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <h2 className="font-serif text-4xl leading-tight text-forest sm:text-5xl">
                Ready to find the right place — together?
              </h2>
              <p className="mt-4 text-base text-muted-foreground sm:text-lg">
                Create a free family account to send drop-in inquiries, save favorite homes, and
                track your trial visits in one calm place.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button asChild className="rounded-full bg-primary px-6 py-6 text-base text-primary-foreground transition-transform hover:-translate-y-0.5">
                  <Link to="/register" data-testid="cta-create-account-btn">
                    Create your family account <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-border bg-transparent px-6 py-6 text-base text-foreground hover:bg-muted"
                >
                  <Link to="/for-facilities" data-testid="cta-for-facilities-btn">
                    I run a care home
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-3">
                <img
                  src={HERO_IMG}
                  alt="Caregiver moment"
                  className="aspect-[3/4] w-full rounded-2xl object-cover"
                />
                <div className="flex flex-col gap-3">
                  <img
                    src={SUPPORT_IMG}
                    alt="Community moment"
                    className="aspect-square w-full rounded-2xl object-cover"
                  />
                  <Card className="flex-1 rounded-2xl border border-gold/40 bg-gold/10 p-4">
                    <p className="font-serif text-lg text-forest leading-tight">
                      Don't see a home in your area yet?
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Our network is growing — call us directly and we'll help you find care today.
                    </p>
                    <a
                      href="tel:+15863068449"
                      data-testid="landing-cta-phone-2"
                      className="mt-3 inline-flex items-center gap-2 rounded-full bg-gold/20 px-3 py-1.5 text-sm font-semibold text-forest hover:bg-gold/30"
                    >
                      <Phone className="h-3.5 w-3.5 text-gold" /> (586) 306-8449
                    </a>
                    <p className="mt-2 text-xs italic text-muted-foreground">Always available — 24/7</p>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
}
